// =========================
// DataSourcePanel — v1.2.0 Workstream 4 (Admin Portal)
//
// Data source control panel. Reads/writes data_source_config via the
// admin-get-source-config / admin-update-source-config Netlify functions.
// Toggle a source on/off and the next analysis request sees the new state
// (sourceRouter.js reads the table per request).
//
// Extracted from AdminDashboard in Phase 2 so the dashboard could become a
// tabbed shell hosting multiple panels (Data Sources, Users, …). This panel
// provides no page chrome — the shell supplies layout + background.
// =========================

import React, { useEffect, useState, useCallback } from 'react';
import { listSourceConfig, updateSourceConfig } from '../../lib/admin';

export default function DataSourcePanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: loadError } = await listSourceConfig();
    if (loadError) {
      setError(loadError.message || 'Could not load data source config');
      setRows([]);
    } else {
      setRows(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  async function handleToggle(row) {
    const { error: updateError } = await updateSourceConfig({
      source_key: row.source_key,
      enabled:    !row.enabled,
      priority:   row.priority,
    });
    if (!updateError) {
      await loadRows();
    }
    // Update-side errors are not surfaced inline in this phase. The
    // re-fetch will simply show the unchanged row, which is the correct
    // user-visible signal that the toggle did not stick. A toast pattern
    // is in the roadmap for v1.3.0.
  }

  if (loading) {
    return (
      <div
        style={{
          padding: 'var(--space-5)',
          color: 'var(--color-steel-lt)',
          fontSize: 'var(--text-base)',
        }}
      >
        Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div
        role="alert"
        style={{
          padding: 'var(--space-4) var(--space-5)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-danger)',
          background: 'var(--color-danger-ghost)',
          color: 'var(--color-danger-lt)',
          fontSize: 'var(--text-base)',
          fontWeight: 'var(--weight-semibold)',
        }}
      >
        {error}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div
        style={{
          padding: 'var(--space-5)',
          color: 'var(--color-muted)',
          fontStyle: 'italic',
        }}
      >
        No data sources configured
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      {rows.map((row) => (
        <div
          key={row.id || row.source_key}
          className="card"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--space-4)',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
            <span
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--text-xl)',
                fontWeight: 'var(--weight-bold)',
                color: 'var(--color-white)',
              }}
            >
              {row.source_key}
            </span>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-steel)' }}>
              Priority: {row.priority}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <span className={`badge ${row.enabled ? 'badge-active' : 'badge-pass'}`}>
              {row.enabled ? 'ON' : 'OFF'}
            </span>
            <button
              type="button"
              onClick={() => handleToggle(row)}
              className={row.enabled ? 'btn btn-secondary' : 'btn btn-primary'}
            >
              {row.enabled ? 'Disable' : 'Enable'} {row.source_key}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
